import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/en.json';

try { countries.registerLocale(require('i18n-iso-countries/langs/en.json')); } catch {}

const normalizeCountryCode = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (/^[a-z]{2}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  try {
    const code = countries.getAlpha2Code(trimmed, 'en');
    return code ? code.toUpperCase() : '';
  } catch {
    return '';
  }
};

const toCountryName = (value) => {
  const code = normalizeCountryCode(value);
  if (!code) return value || '';
  try {
    return countries.getName(code, 'en') || code;
  } catch {
    return code;
  }
};

const OptionRow = ({ code, label, country }) => (
  <div className="d-flex align-items-center" style={{ height: 40, whiteSpace: 'nowrap', overflow: 'hidden' }}>
    {code ? (
      <img
        src={`https://flagcdn.com/20x15/${code}.png`}
        alt=""
        width="20"
        height="15"
        className="me-2"
        style={{ display: 'block', borderRadius: 2, objectFit: 'cover' }}
        loading="lazy"
      />
    ) : (
      <span className="me-2">🏳️</span>
    )}
    <span className="fw-semibold me-2 text-truncate" style={{ maxWidth: 180 }}>{label}</span>
    {country && (
      <span className="text-body-secondary small text-truncate" style={{ maxWidth: 160 }}>
        {country}
      </span>
    )}
  </div>
);

const Option = (props) => {
  const { data, innerProps, isFocused } = props;
  return (
    <div
      {...innerProps}
      className={`px-2 ${isFocused ? 'bg-body-tertiary' : ''}`}
      title={`${data.label}${data.country ? ` · ${data.country}` : ''}`}
    >
      <OptionRow code={data.code} label={data.label} country={data.country} />
    </div>
  );
};

const SingleValue = ({ data }) => (
  <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
    <OptionRow code={data.code} label={data.label} country={null} />
  </div>
);

const detectMobileOrTouch = () => {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  if (typeof navigator !== 'undefined') {
    if (navigator.maxTouchPoints > 0) return true;
    if ('msMaxTouchPoints' in navigator && navigator.msMaxTouchPoints > 0) return true;
  }
  const width = window.innerWidth || 0;
  try {
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    return coarse || width < 768;
  } catch {
    return width < 768;
  }
};

const buildStyles = (invalid, menuOpen, zIndex = 2100) => ({
  control: (base, state) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    borderRadius: '.5rem',
    borderColor: invalid ? 'var(--bs-danger)' : 'var(--bs-border-color)',
    boxShadow: 'none',
    minHeight: 40,
    height: 40,
    cursor: 'pointer',
    backgroundColor: 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)',
    ':hover': {
      borderColor: invalid ? 'var(--bs-danger)' : (state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)'),
    },
  }),
  valueContainer: (b) => ({
    ...b,
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
  }),
  indicatorsContainer: (b) => ({ ...b, flexShrink: 0, height: 40, alignItems: 'center' }),
  dropdownIndicator: (b) => ({ ...b, padding: 6 }),
  clearIndicator: (b) => ({ ...b, padding: 6 }),
  singleValue: (b) => ({ ...b, marginLeft: 0, marginRight: 0, maxWidth: '100%', color: 'var(--bs-body-color)' }),
  menuPortal: (b) => ({
    ...b,
    zIndex,
    pointerEvents: menuOpen ? 'auto' : 'none',
  }),
  menu: (b) => ({
    ...b,
    zIndex,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)',
    border: '1px solid var(--bs-border-color)',
  }),
  menuList: (b) => ({
    ...b,
    backgroundColor: 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 8,
  }),
  option: (b, state) => ({
    ...b,
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: state.isFocused
      ? 'var(--bs-primary-bg-subtle)'
      : 'var(--bs-body-bg)',
    color: 'var(--bs-body-color)',
    ':active': {
      backgroundColor: 'var(--bs-primary-bg-subtle)',
    },
  }),
});

/**
 * agents: [{id, firstName, lastName, email, phone, country}, ...]
 * country: optional string to filter agents by exact country name
 * value: current agentId (string|number|''|null)
 * onChange: (agentId|null) => void
 * invalid: bool for red border
 */
export default function ChooseAgentOption({
  agents = [],
  country,
  value,
  onChange,
  placeholder = '-- Select Agent --',
  isDisabled = false,
  invalid = false,
  instanceId = 'agentId',
  inputId = 'agentId',
  autoScrollIntoView = true,
}) {
  const filtered = useMemo(() => {
    const normalizedCountry = normalizeCountryCode(country);
    const source = Array.isArray(agents) ? agents.filter(a => a?.active !== false) : [];
    const list = normalizedCountry
      ? source.filter(a => normalizeCountryCode(a.country) === normalizedCountry)
      : source;
    return list.map(a => {
      const label = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.email || `Agent #${a.id}`;
      const countryCode = normalizeCountryCode(a.country);
      return {
        value: String(a.id),
        label,
        sub: a.phone || a.email || '',
        code: countryCode ? countryCode.toLowerCase() : "",
        country: toCountryName(a.country),
      };
    }).sort((x, y) => x.label.localeCompare(y.label));
  }, [agents, country]);

  const selected = useMemo(
    () => filtered.find(o => o.value === String(value ?? '')) || null,
    [filtered, value]
  );

  const selectRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isTouchInput, setIsTouchInput] = useState(() => detectMobileOrTouch());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = () => setIsTouchInput(detectMobileOrTouch());
    update();

    let mql;
    try {
      mql = window.matchMedia('(pointer: coarse)');
    } catch {
      mql = null;
    }

    if (mql?.addEventListener) {
      mql.addEventListener('change', update);
    } else if (mql?.addListener) {
      mql.addListener(update);
    }

    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
      if (!mql) return;
      if (mql.removeEventListener) {
        mql.removeEventListener('change', update);
      } else if (mql.removeListener) {
        mql.removeListener(update);
      }
    };
  }, []);

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
    if (autoScrollIntoView) {
      const control = selectRef.current?.controlRef;
      control?.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    }
  }, [autoScrollIntoView]);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const isSearchable = !isTouchInput;

  const handleFocus = useCallback(() => {
    if (autoScrollIntoView) {
      const control = selectRef.current?.controlRef;
      control?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }, [autoScrollIntoView]);

  const handleBlur = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleCloseOnScroll = useCallback((event) => {
    const menuList = selectRef.current?.menuListRef;
    if (!menuList || !event) return true;
    const target = event.target;
    if (typeof Node !== 'undefined' && target instanceof Node && menuList.contains(target)) {
      return false;
    }
    return true;
  }, []);

  return (
    <Select
      classNamePrefix="rs"
      inputId={inputId}
      instanceId={instanceId}
      placeholder={placeholder}
      options={filtered}
      value={selected}
      onChange={(opt) => onChange(opt?.value || '')}
      isClearable
      isDisabled={isDisabled || filtered.length === 0}
      isSearchable={isSearchable}
      styles={buildStyles(invalid, menuOpen)}
      components={{ Option, SingleValue, IndicatorSeparator: null }}
      menuPosition="fixed"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuShouldBlockScroll={false}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={handleCloseOnScroll}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      onFocus={handleFocus}
      onBlur={handleBlur}
      openMenuOnFocus
      openMenuOnClick
      ref={selectRef}
      noOptionsMessage={() => (country ? 'No agents' : 'No agents')}
    />
  );
}
